import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Sparkles } from "lucide-react";
import CoupleAccent from "@/components/CoupleAccent";

const makeBurst = () =>
  Array.from({ length: 32 }, (_, i) => ({
    id: `${Date.now()}-${i}`,
    left: 50 + (Math.random() - 0.5) * 90,
    size: 14 + Math.random() * 26,
    duration: 3.5 + Math.random() * 2.5,
    delay: Math.random() * 0.5,
    rotate: Math.random() * 360,
    hue: 340 + Math.random() * 20,
  }));

export const VirtualHug = () => {
  const [bursts, setBursts] = useState([]);
  const [showMessage, setShowMessage] = useState(false);

  const trigger = () => {
    const b = makeBurst();
    setBursts((prev) => [...prev, ...b]);
    setShowMessage(true);
    setTimeout(() => {
      setBursts((prev) => prev.filter((x) => !b.includes(x)));
    }, 6500);
    setTimeout(() => setShowMessage(false), 5000);
  };

  return (
    <section
      data-testid="hug-section"
      className="relative py-24 sm:py-32 px-6 text-center overflow-hidden"
    >
      <CoupleAccent
        src="/couple/holding_hands.png"
        alt="us together"
        className="hidden md:block absolute bottom-10 left-4 lg:left-24 w-24 lg:w-32 -rotate-[6deg] pointer-events-none"
        float={{ y: [0, -9, 0], rotate: [-6, -3, -6] }}
        duration={7}
      />
      <div className="max-w-xl mx-auto">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-hand text-2xl text-rose-400 mb-2"
        >
          if i could,
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="font-serif-love text-4xl sm:text-5xl text-[#3A2C2C]"
        >
          i'd hold you right now.
        </motion.h2>
        <p className="font-body-love text-[#7D6B6B] mt-5 max-w-md mx-auto">
          until i can, press this. it's the tightest, warmest hug i can send
          through a screen.
        </p>

        <motion.button
          data-testid="virtual-hug-button"
          onClick={trigger}
          whileHover={{ scale: 1.05, y: -3 }}
          whileTap={{ scale: 0.94 }}
          className="mt-10 inline-flex items-center gap-3 px-9 py-4 rounded-full bg-white border border-[#E8D8D8] text-[#C28080] font-medium shadow-md shadow-rose-100 hover:bg-[#F5EBEB] transition-colors"
        >
          <Heart className="w-5 h-5" fill="currentColor" strokeWidth={0} />
          send me a hug
          <Sparkles className="w-4 h-4" />
        </motion.button>

        <AnimatePresence>
          {showMessage && (
            <motion.p
              key="hug-msg"
              data-testid="hug-message"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="font-hand text-3xl text-[#C28080] mt-8"
            >
              consider yourself squeezed — tightly, for a long moment.
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {bursts.map((b) => (
        <span
          key={b.id}
          className="heart-burst"
          style={{
            left: `${b.left}%`,
            bottom: "10%",
            width: b.size,
            height: b.size,
            color: `hsl(${b.hue}, 60%, 72%)`,
            animationDuration: `${b.duration}s`,
            animationDelay: `${b.delay}s`,
            transform: `rotate(${b.rotate}deg)`,
          }}
        >
          <Heart className="w-full h-full" fill="currentColor" strokeWidth={0} />
        </span>
      ))}
    </section>
  );
};

export default VirtualHug;
